/**
 * `people` — centred overlapping avatars, `+N` past 3 (M2 §5.7, §6; measured `[B5§8.1]`).
 * An unknown or deleted person renders the outlined placeholder monday shows.
 */
import { getColumnType, maxPeopleAllowed, type PeopleSettings, type PeopleValue } from '@elizmonday/shared';
import { useState } from 'react';
import { valueContext } from '../context';
import { CellPopover, PersonAvatar, UnknownAvatar, useEditorDraft } from '../primitives';
import { defineColumnUi, type CellRenderContext } from '../types';
import { sharedBuckets } from '../buckets';
import type { Person } from '../../data/types';
import { PeopleSettingsDialog } from './SettingsDialog';

const shared = getColumnType('people');

export function AvatarStack({
  ids,
  context,
  limit = 3,
}: {
  ids: readonly number[];
  context: CellRenderContext;
  limit?: number;
}) {
  const byId = new Map(context.people.map((person) => [String(person.id), person]));
  const shown = ids.slice(0, limit);
  return (
    <span className="ez-avatars">
      {shown.map((id) => {
        const person = byId.get(String(id));
        return person ? (
          <PersonAvatar key={id} name={person.name} color={person.color} photoUrl={person.photoUrl} />
        ) : (
          <UnknownAvatar key={id} />
        );
      })}
      {ids.length > limit ? <span className="ez-avatars__more">{`+${ids.length - limit}`}</span> : null}
    </span>
  );
}

/**
 * The display-name lookup the people-like types hand to `toText` (P-126). `EntityRef` ids are
 * numeric and `Person.id` is a branded string, so the comparison is on `String(...)` — the same
 * one `AvatarStack` makes.
 */
export function nameResolver(context: CellRenderContext): (ref: { id: number }) => string | undefined {
  const byId = new Map(context.people.map((person) => [String(person.id), person.name]));
  return (ref) => byId.get(String(ref.id));
}

/**
 * monday's people picker, measured on the sandbox board
 * (`discovery/ui-reference/editors/people/editor.json`, parity card P-004):
 *
 *   popover        360 x 286, white, radius 8, padding 16
 *   combobox       a 328 x 32 chips + input row, radius 4, then the suggestion list
 *   notify bar     328 x 48, `#cce5ff`, radius 8, padding 8 — "Assignees will be notified"
 *                  with a 20 px icon on the left and a 51 x 32 `Mute` button on the right
 *   divider        328 x 1 `#d0d4e4`
 *   AI row         166 x 32 `Auto-assign people` (absent here: no AI, open question 223)
 *
 * The chips are the picked people, each removable; the list below is headed
 * "Suggested people" and is the board's members minus the picked ones.
 */
export const PEOPLE_PICKER_WIDTH = 360;

export function PeoplePicker({
  people,
  picked,
  max,
  onToggle,
  onDismiss,
  onCancel,
}: {
  people: readonly Person[];
  picked: readonly number[];
  max: number | null;
  onToggle(id: number): void;
  /** The outside click, which commits the picked set. */
  onDismiss(): void;
  /** `Escape`, which discards it (P-121). */
  onCancel?(): void;
}) {
  const [query, setQuery] = useState('');
  const [muted, setMuted] = useState(false);
  const byId = new Map(people.map((person) => [Number(person.id), person]));
  const term = query.trim().toLowerCase();
  const suggestions = people.filter(
    (person) => !picked.includes(Number(person.id)) && person.name.toLowerCase().includes(term),
  );
  const full = max !== null && picked.length >= max;

  return (
    <CellPopover
      label="Assign people"
      width={PEOPLE_PICKER_WIDTH}
      onDismiss={onDismiss}
      onCancel={onCancel}
    >
      <div className="ez-people-picker__combobox">
        {picked.map((id) => {
          const person = byId.get(id);
          return (
            <span key={id} className="ez-people-chip" data-testid={`people-chip-${String(id)}`}>
              {person ? (
                <PersonAvatar name={person.name} color={person.color} photoUrl={person.photoUrl} size={20} />
              ) : (
                <UnknownAvatar size={20} />
              )}
              <span className="ez-people-chip__name">{person?.name ?? `#${String(id)}`}</span>
              <button
                type="button"
                className="ez-people-chip__remove"
                data-testid={`chip-remove-${String(id)}`}
                aria-label={`Remove ${person?.name ?? String(id)}`}
                onClick={() => onToggle(id)}
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          className="ez-people-picker__input"
          // The picker is opened deliberately by a click on the cell; focus belongs in it.
          autoFocus
          aria-label="Search people"
          placeholder={picked.length === 0 ? 'Search names' : ''}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>

      <p className="ez-people-picker__section">Suggested people</p>
      <ul className="ez-option-list" role="listbox" aria-label="People" aria-multiselectable="true">
        {suggestions.map((person) => (
          <li key={person.id}>
            <button
              type="button"
              role="option"
              aria-selected={false}
              disabled={full}
              className="ez-option-list__option"
              data-testid={`people-option-${String(person.id)}`}
              onClick={() => onToggle(Number(person.id))}
            >
              <PersonAvatar name={person.name} color={person.color} photoUrl={person.photoUrl} size={20} />
              {person.name}
            </button>
          </li>
        ))}
        {suggestions.length === 0 ? (
          <li className="ez-option-list__empty">{full ? 'This column is full' : 'No one matches'}</li>
        ) : null}
      </ul>

      {/* monday's notify bar; `Mute` turns the assignment notification off for this write. */}
      <div className="ez-people-picker__notify" data-muted={muted || undefined}>
        <span className="ez-people-picker__notify-text">
          {muted ? 'Assignees will not be notified' : 'Assignees will be notified'}
        </span>
        <button
          type="button"
          className="ez-people-picker__mute"
          data-testid="people-mute"
          onClick={() => setMuted((current) => !current)}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
      </div>
    </CellPopover>
  );
}

export const peopleColumnUi = defineColumnUi<PeopleValue, PeopleSettings>({
  type: 'people',
  icon: 'columnPeople',
  gallery: { group: 'people', keywords: ['people', 'owner', 'assignee', 'person', 'team'] },
  /** P-022: `174316936` `Executor` (`width: null`) renders **98** px in monday. */
  defaultWidth: 98,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, context }) => (
    <AvatarStack ids={(value?.personsAndTeams ?? []).map((ref) => ref.id)} context={context} />
  ),

  /** **P-282**: the picked set follows the store while untouched; an untouched dismiss writes
   * nothing (`useEditorDraft`). */
  Editor: ({ value, settings, context, onCommit, onCancel }) => {
    const editor = useEditorDraft<number[]>((value?.personsAndTeams ?? []).map((ref) => ref.id));
    const picked = editor.draft;
    const max = maxPeopleAllowed(settings);
    return (
      <PeoplePicker
        people={context.people}
        picked={picked}
        max={max}
        onToggle={(id) =>
          editor.update((current) =>
            current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
          )
        }
        onDismiss={() =>
          editor.dismiss(
            () =>
              picked.length === 0
                ? onCommit(null)
                : onCommit({
                    personsAndTeams: picked.map((id) => ({ id, kind: 'person' as const })),
                  } as PeopleValue),
            onCancel,
          )
        }
        onCancel={onCancel}
      />
    );
  },

  SettingsDialog: PeopleSettingsDialog,

  fromClipboard(text, settings, context) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const ids = trimmed.split(',').map((part) => {
      const name = part.trim();
      const person = context.people.find((entry) => entry.name.toLowerCase() === name.toLowerCase());
      if (!person) throw new Error(`"${name}" is not a member of this board`);
      return Number(person.id);
    });
    return shared.parseValue(
      { personsAndTeams: ids.map((id) => ({ id, kind: 'person' })) },
      settings,
    ) as PeopleValue | null;
  },
  /**
   * **P-126.** `fromClipboard` above parses **names**, so `toClipboard` has to produce them:
   * `toText` documents that the names are not in the cell and that the caller supplies
   * `resolveName` — this call site used to pass `undefined`, so every copy was `""` and the
   * paste that read it wrote nothing. The context is the same one the `AvatarStack` renders
   * from, so the clipboard and the cell agree on every name.
   */
  toClipboard(value, settings, context) {
    return shared.toText(value, settings, { resolveName: nameResolver(context) }) ?? '';
  },
  bucketsOf: sharedBuckets('people'),

  /** A card's cover band: the photo of the first person the cell names `[V2§7.5]`. */
  coverImageOf(value, _settings, context) {
    for (const ref of value?.personsAndTeams ?? []) {
      const person = context.people.find((entry) => String(entry.id) === String(ref.id));
      if (person?.photoUrl) return person.photoUrl;
    }
    return null;
  },

  summarise(aggregation, cells, _settings, context) {
    if (aggregation !== 'avatars') return null;
    const counts = new Map<number, number>();
    for (const cell of cells) {
      for (const ref of cell?.personsAndTeams ?? []) counts.set(ref.id, (counts.get(ref.id) ?? 0) + 1);
    }
    const byId = new Map(context.people.map((person) => [String(person.id), person]));
    const entries = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id, count]) => ({ id, count, person: byId.get(String(id)) ?? null }));
    return {
      aggregation,
      text: entries.length === 0 ? '' : String(entries.length),
      detail: { entries },
    };
  },
});

export { valueContext };
